import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAppVersion as getAppVersionService,
  updateAppVersion as updateAppVersionService,
} from "../../services/MobileAppAlertsService";
import {
  defaultMobileAppAlertsState,
  MobileAppAlertsState,
  UpdateAppVersionParams,
} from "../../interfaces/mobile-app-alerts-interface";

export const getAppVersion = createAsyncThunk(
  "mobileAppAlerts/getAppVersion",
  async (
    appPackageName:
      | "com.malayan.MalayanMobile"
      | "com.malayan.MalayanMobile.TEST",
    thunkAPI
  ) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const appVersionData = await getAppVersionService(appPackageName);
      return appVersionData;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateAppVersion = createAsyncThunk(
  "mobileAppAlerts/updateAppVersion",
  async (appVersionData: UpdateAppVersionParams, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const updateVersionResponse = await updateAppVersionService(
        appVersionData
      );
      return updateVersionResponse;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const mobileAppAlertsSlice = createSlice({
  name: "mobileAppAlerts",
  initialState: defaultMobileAppAlertsState as MobileAppAlertsState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAppVersion.pending, (state) => {
        state.status = "loading";
        state.message = null;
      })
      .addCase(getAppVersion.fulfilled, (state, action) => {
        const appPackageName = action.meta.arg;

        state.appVersions[appPackageName] = action.payload;

        state.status = "succeeded";
        state.error = null;
        state.message = "Successfully retrieved version details.";
      })
      .addCase(getAppVersion.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
        state.message = "Failed to retrieve version details.";
      })
      .addCase(updateAppVersion.pending, (state) => {
        state.status = "loading";
        state.message = null;
      })
      .addCase(updateAppVersion.fulfilled, (state, action) => {
        const appUpdateData = action.meta.arg;
        const updatedApp = appUpdateData.appId;
        const updatedPlatform = appUpdateData.platform;

        state.appVersions[updatedApp].versionData[updatedPlatform] = {
          versionCode: appUpdateData.versionCode,
          versionName: appUpdateData.versionName,
          releaseDate: appUpdateData.releaseDate ?? new Date().toISOString(),
          notes: appUpdateData.notes ?? "",
        };

        state.status = "succeeded";
        state.message = "Successfully updated version details.";
        state.error = null;
      })
      .addCase(updateAppVersion.rejected, (state, action) => {
        state.status = "failed";
        state.message = "Failed to update version details.";
        state.error = action.payload as string;
      });
  },
});

const mobileAppAlertsReducer = mobileAppAlertsSlice.reducer;

export default mobileAppAlertsReducer;
